import { Text } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

// The PDF bind panel's catalogue, reused rather than re-drawn. The two lists offer the same
// data points for the same job, so a second set of styles would only be a second look to keep
// in step — and this one is the established one.
import { iconForGroup } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/bindPanel.utils';
import {
	DataPointIconTile,
	GroupLabel,
	GroupSection,
	RowText,
	SampleValuePill,
	SearchInput,
} from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/bindPanel.styled';

import { filterFieldItems } from './extensions/fieldSuggestion';
import type { FieldSuggestionItem } from './extensions/fieldSuggestion';
import { FIELD_DRAG_MIME, serialiseFieldPayload } from './extensions/fieldDrop';
import { groupFields } from './fieldPalette.utils';
import { PaletteList, PaletteRow } from './flowDocumentEditor.styled';

interface Props {
	fields: FieldSuggestionItem[];
	/** Inserts at the caret. There is no add button: clicking the data point IS the action,
	 *  and dragging it puts it somewhere other than the caret. */
	onInsert?: (field: FieldSuggestionItem) => void;
}

/**
 * The data points an author can drag into the document, under the object each belongs to.
 *
 * Dragging carries a private MIME payload, never `text/plain`: dropped as text the field would
 * arrive as the literal `{{ name }}` and be one autocorrect away from a blank in an executed
 * deed, which is the failure the atom node exists to prevent.
 *
 * An entry shows its label and the value it resolves to for the recipient being previewed. The
 * token it inserts is deliberately not shown — a field name is this system's internal key, and
 * an analyst drafting a share transfer has no reason to read one.
 *
 * Clicking a row inserts it at the caret; dragging puts it where it lands. No add button: with
 * both of those, a third control doing the same thing is only another thing to explain.
 */
export const FieldPalette = ({ fields, onInsert }: Props) => {
	const [query, setQuery] = useState('');
	const groups = useMemo(() => groupFields(filterFieldItems(fields, query)), [fields, query]);

	return (
		<>
			<SearchInput
				placeholder="Search data points"
				radius="md"
				leftSection={<IconSearch size={14} />}
				value={query}
				data-testid="field-palette-search"
				onChange={(event) => setQuery(event.currentTarget.value)}
			/>
			{groups.length === 0 ? (
				<Text size="sm" c="dark.0">
					No data point matches that search.
				</Text>
			) : (
				<PaletteList data-testid="field-palette">
					{groups.map((group) => {
						const GroupIcon = iconForGroup(group.name);
						return (
							<GroupSection
								key={group.name}
								data-testid={`field-palette-group-${group.name}`}
							>
								<GroupLabel>
									<GroupIcon size={14} />
									{/* Not `dimmed`: v7 resolves that token to the light end of the
									    grey ramp, which is what made this heading a smudge. */}
									<Text size="xs" fw={600} c="dark.0">
										{group.name}
									</Text>
								</GroupLabel>
								{group.items.map((field) => (
									<PaletteRow
										key={field.name}
										draggable
										role="button"
										tabIndex={0}
										aria-label={`Insert ${field.label}`}
										data-testid={`field-palette-${field.name}`}
										onDragStart={(event) => {
											event.dataTransfer.setData(
												FIELD_DRAG_MIME,
												serialiseFieldPayload(field)
											);
											event.dataTransfer.effectAllowed = 'copy';
										}}
										onClick={() => onInsert?.(field)}
										onKeyDown={(event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												event.preventDefault();
												onInsert?.(field);
											}
										}}
									>
										<DataPointIconTile>
											<GroupIcon size={16} />
										</DataPointIconTile>
										<RowText>
											<Text size="sm" fw={500}>
												{field.label}
											</Text>
											{field.firstOfList === true && (
												// The path is `…[0]`, so an investor with three
												// securities gets one. Said here because the
												// label cannot show it.
												<Text size="xs" c="dark.0">
													First item in the list only
												</Text>
											)}
										</RowText>
										{field.sample !== undefined && (
											<SampleValuePill title={field.sample}>
												<Text size="xs" c="dark.5">
													{field.sample}
												</Text>
											</SampleValuePill>
										)}
									</PaletteRow>
								))}
							</GroupSection>
						);
					})}
				</PaletteList>
			)}
		</>
	);
};
